"use client";

import Link from "next/link";
import { Check, Facebook, Instagram, Youtube, Mail, Phone, MapPin } from "lucide-react";

export default function Footer() {
  return (
    <footer className="mt-auto bg-primary border-t border-white/5 pt-16 pb-6">
      <div className="max-w-7xl mx-auto px-6">
        {/* CTA Block — улучшенный, без лишних скруглений */}
        <div className="mb-16 pb-16 border-b border-white/5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl text-center md:text-left">
              <h2 className="font-heading text-2xl font-bold text-text-main sm:text-3xl lg:text-4xl">
                Soovid rohkem informatsiooni?
              </h2>
              <p className="mt-3 text-text-muted text-base sm:text-lg leading-relaxed">
                Tutvuge iga teenusega üksikasjalikumalt või võtke meiega ühendust oma autokaitse raskuste lahendamiseks.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto shrink-0">
              <Link
                href="/teenused"
                className="rounded-sm border border-white/10 px-8 py-3.5 text-center font-heading font-bold text-text-main transition-all hover:border-accent hover:text-accent hover:bg-white/5"
              >
                Vaata teenuseid
              </Link>
              <Link
                href="/kontakt"
                className="rounded-sm bg-accent px-8 py-3.5 text-center font-heading font-bold text-primary transition-all hover:bg-accent/90 hover:shadow-lg hover:shadow-accent/10"
              >
                Võta ühendust
              </Link>
            </div>
          </div>
        </div>

        {/* Основная сетка футера */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 pb-16">
          {/* Колонка 1 — Бренд */}
          <div className="flex flex-col items-start">
            <span className="font-heading font-extrabold text-2xl text-text-main tracking-tight leading-none mb-2">
              PPF Garage
            </span>
            <span className="text-text-muted text-sm font-body mb-6">
              Premium Vehicle Protection Studio
            </span>
            <div className="flex items-center gap-2 text-text-main font-body text-sm">
              <Check className="w-4 h-4 text-accent shrink-0" />
              <span>Koolitatud CarPro spetsialistid</span>
            </div>
          </div>

          {/* Колонка 2 — Навигация */}
          <div className="flex flex-col">
            <h3 className="text-accent uppercase text-xs tracking-[0.1em] font-bold mb-5 font-body">
              Leheküljed
            </h3>
            <ul className="flex flex-col gap-3 font-body text-sm text-text-muted">
              <li>
                <Link href="/" className="hover:text-text-main transition-colors">
                  Avaleht
                </Link>
              </li>
              <li>
                <Link href="/meist" className="hover:text-text-main transition-colors">
                  Meist
                </Link>
              </li>
              <li>
                <Link href="/teenused" className="hover:text-text-main transition-colors">
                  Teenused
                </Link>
              </li>
              <li>
                <Link href="/hinnakiri" className="hover:text-text-main transition-colors">
                  Hinnakiri
                </Link>
              </li>
              <li>
                <Link href="/kontakt" className="hover:text-text-main transition-colors">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Колонка 3 — Услуги */}
          <div className="flex flex-col">
            <h3 className="text-accent uppercase text-xs tracking-[0.1em] font-bold mb-5 font-body">
              Teenused
            </h3>
            <ul className="flex flex-col gap-3 font-body text-sm text-text-muted">
              <li>
                <Link href="/teenused/auto-kiletamine" className="hover:text-text-main transition-colors">
                  Kaitsekiletamine PPF
                </Link>
              </li>
              <li>
                <Link href="/teenused/keraamiline-kaitse" className="hover:text-text-main transition-colors">
                  Keraamiline kaitse
                </Link>
              </li>
              <li>
                <Link href="/teenused/klaaside-toonimine" className="hover:text-text-main transition-colors">
                  Klaaside toonimine
                </Link>
              </li>
            </ul>
          </div>

          {/* Колонка 4 — Контакты + иконки */}
          <div className="flex flex-col">
            <h3 className="text-accent uppercase text-xs tracking-[0.1em] font-bold mb-5 font-body">
              Kontakt
            </h3>
            <ul className="flex flex-col gap-3 font-body text-sm text-text-muted mb-6">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>Tähnase tee 1, Peetri alevik, 75312</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-accent shrink-0" />
                <a href="mailto:info@ppf.ee" className="hover:text-text-main transition-colors">
                  info@ppf.ee
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-accent shrink-0" />
                <a href="tel:+3726803510" className="hover:text-text-main transition-colors">
                  +372 680 3510
                </a>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-text-muted">E–R 09:00–18:00</span>
              </li>
            </ul>

            {/* Социальные иконки */}
            <div className="flex items-center gap-4">
              <a
                href="https://www.facebook.com/ppfgarage"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-text-muted hover:text-accent transition-colors duration-200"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/ppfgarage"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-text-muted hover:text-accent transition-colors duration-200"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@ppfgarage"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="text-text-muted hover:text-accent transition-colors duration-200"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Нижняя часть — копирайт и рег. номер */}
        <div className="border-t border-white/5 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-text-muted font-body text-xs tracking-wide">
            © {new Date().getFullYear()} PPF Garage OÜ. Kõik õigused kaitstud.
          </p>
          <p className="text-text-muted font-body text-xs tracking-wide">
            Reg. nr 12955543
          </p>
        </div>
      </div>
    </footer>
  );
}